import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  Select,
  useColorModeValue,
  Alert,
  AlertIcon,
  Spinner,
  Progress,
} from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getPublicForm,
  getMyFormSubmission,
  saveFormSubmission,
  getFormFillerToken,
  clearFormFillerSession,
  sendFormFillOtp,
  verifyFormFillOtp,
  setFormFillerToken,
} from '../../services/formService';

const IR_MOBILE = /^09\d{9}$/;

/** دکمه‌های اصلی و بازگشت — همان الگوی صفحه رزرو نوبت */
function FormActionButtons({ primary, back }) {
  return (
    <Stack spacing={3} w="full">
      {primary}
      {back}
    </Stack>
  );
}

export default function PublicFormFill() {
  const { formId } = useParams();
  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('#E6F5F3', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'white');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const [isAuthed, setIsAuthed] = useState(Boolean(getFormFillerToken()));
  const [authStep, setAuthStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    setStep(0);
    setSubmitted(false);
    setInfo('');
    setError('');
  }, [formId]);

  const loadFormData = useCallback(async () => {
    const formRes = await getPublicForm(formId);
    const f = formRes.data?.data?.form;
    if (!f) throw new Error('فرم یافت نشد');
    setForm(f);

    if (getFormFillerToken()) {
      try {
        const subRes = await getMyFormSubmission(formId);
        const sub = subRes.data?.data;
        if (sub?.hasSubmission && sub.submission?.answers) {
          setAnswers({ ...sub.submission.answers });
          setInfo('پاسخ قبلی شما بارگذاری شد؛ می‌توانید مرحله‌به‌مرحله ویرایش کنید.');
        } else {
          setAnswers({});
        }
      } catch (e) {
        if (e.response?.status === 401) {
          clearFormFillerSession();
          setIsAuthed(false);
          setAnswers({});
        } else {
          throw e;
        }
      }
    }
  }, [formId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setForm(null);
      try {
        await loadFormData();
        if (!cancelled) {
          setIsAuthed(Boolean(getFormFillerToken()));
        }
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.message || 'خطا در بارگذاری فرم');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, loadFormData]);

  const fields = form?.fields || [];
  const total = fields.length;
  const currentField = total > 0 ? fields[step] : null;

  const setField = (key, value) => {
    setError('');
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const validateField = useCallback(
    (field) => {
      if (!field) return true;
      const raw = answers[field.key];
      const empty =
        raw === undefined ||
        raw === null ||
        (typeof raw === 'string' && raw.trim() === '');

      if (field.required && empty) {
        setError(`لطفاً «${field.label}» را تکمیل کنید.`);
        return false;
      }
      if (!empty && field.type === 'phone') {
        const s = String(raw).trim();
        if (!IR_MOBILE.test(s)) {
          setError('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود.');
          return false;
        }
      }
      return true;
    },
    [answers]
  );

  const validateAllFields = useCallback(() => {
    for (const field of fields) {
      if (!validateField(field)) return false;
    }
    setError('');
    return true;
  }, [fields, validateField]);

  const goNext = () => {
    if (!currentField) return;
    if (!validateField(currentField)) return;
    setError('');
    if (step < total - 1) {
      setStep((s) => s + 1);
    }
  };

  const goPrev = () => {
    setError('');
    if (step > 0) setStep((s) => s - 1);
  };

  const handleSendOtp = async () => {
    setError('');
    if (!IR_MOBILE.test(phone)) {
      setError('شماره موبایل باید ۱۱ رقم و با ۰۹ شروع شود');
      return;
    }
    setAuthLoading(true);
    try {
      await sendFormFillOtp(phone);
      toast.success('کد تأیید ارسال شد');
      setAuthStep(2);
    } catch (e) {
      setError(e.response?.data?.message || 'ارسال کد ناموفق بود');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setAuthLoading(true);
    try {
      const res = await verifyFormFillOtp(phone, otp);
      const { token } = res.data.data || {};
      if (!token) throw new Error('توکن دریافت نشد');
      setFormFillerToken(token);
      localStorage.setItem('formFillerPhone', phone);
      setIsAuthed(true);
      setAuthStep(1);
      setOtp('');
      toast.success('شماره تأیید شد؛ اکنون می‌توانید فرم را تکمیل کنید.');
      await loadFormData();
    } catch (e) {
      setError(e.response?.data?.message || 'کد تأیید نامعتبر است');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleFinalSave = async () => {
    if (!validateAllFields()) return;

    setSaving(true);
    setError('');
    try {
      const res = await saveFormSubmission(formId, answers);
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message || 'ذخیره ناموفق بود');
      }
      setSubmitted(true);
      toast.success('پاسخ شما ثبت شد');
      const doneText = (form?.completionMessage || '').trim();
      setInfo(doneText || 'پاسخ شما با موفقیت ثبت شد.');
    } catch (err) {
      if (err.response?.status === 401) {
        clearFormFillerSession();
        setIsAuthed(false);
        setError('نشست شما منقضی شده؛ لطفاً دوباره شماره موبایل را تأیید کنید.');
        return;
      }
      setError(err.response?.data?.message || err.message || 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const backToListButton = (
    <Button as={Link} to="/auth/forms" variant="outline" size="md" w="full">
      بازگشت به لیست فرم‌ها
    </Button>
  );

  const renderFieldInput = (field) => {
    if (!field) return null;
    if (field.type === 'textarea') {
      return (
        <Textarea
          value={answers[field.key] ?? ''}
          onChange={(e) => setField(field.key, e.target.value)}
          rows={5}
          size="lg"
          isDisabled={submitted}
        />
      );
    }
    if (field.type === 'text') {
      return (
        <Input
          value={answers[field.key] ?? ''}
          onChange={(e) => setField(field.key, e.target.value)}
          size="lg"
          isDisabled={submitted}
        />
      );
    }
    if (field.type === 'phone') {
      return (
        <Input
          placeholder="09123456789"
          value={answers[field.key] ?? ''}
          onChange={(e) =>
            setField(field.key, e.target.value.replace(/\D/g, '').slice(0, 11))
          }
          maxLength={11}
          size="lg"
          isDisabled={submitted}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <Select
          placeholder="انتخاب کنید"
          value={answers[field.key] ?? ''}
          onChange={(e) => setField(field.key, e.target.value)}
          size="lg"
          isDisabled={submitted}
        >
          {(field.options || []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Select>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Flex minH="100vh" bg={pageBg} align="center" justify="center" dir="rtl">
        <Spinner size="lg" color="teal.500" />
      </Flex>
    );
  }

  if (!form) {
    return (
      <Flex minH="100vh" bg={pageBg} align="center" justify="center" px={4} dir="rtl">
        <Box bg={cardBg} p={8} borderRadius="xl" maxW="md" w="100%">
          <Alert status="error">
            <AlertIcon />
            {error || 'فرم در دسترس نیست.'}
          </Alert>
          <Box mt={4}>{backToListButton}</Box>
        </Box>
      </Flex>
    );
  }

  const isLastStep = total === 0 || step === total - 1;
  const progressPercent = total > 0 ? ((step + 1) / total) * 100 : 100;

  return (
    <Flex minH="100vh" bg={pageBg} align="flex-start" justify="center" px={4} py={10} dir="rtl">
      <Box w="100%" maxW="lg" bg={cardBg} borderRadius="2xl" boxShadow="xl" p={{ base: 5, md: 8 }}>
        <Stack spacing={5}>
          <Box>
            <Heading size="md" color={textColor}>
              {form.title}
            </Heading>
            {form.description ? (
              <Text fontSize="sm" color="gray.600" mt={2}>
                {form.description}
              </Text>
            ) : null}
          </Box>

          {!isAuthed && (
            <Box borderWidth="1px" borderRadius="xl" p={4} borderColor="teal.200" bg="teal.50">
              <Text fontWeight="semibold" color="teal.800" mb={3}>
                تأیید شماره موبایل
              </Text>
              <Text fontSize="sm" color="gray.600" mb={4}>
                برای ثبت پاسخ این فرم، ابتدا شماره موبایل خود را با کد پیامکی تأیید کنید.
              </Text>
              {authStep === 1 ? (
                <Stack spacing={4}>
                  <FormControl>
                    <FormLabel>شماره موبایل</FormLabel>
                    <Input
                      placeholder="09123456789"
                      value={phone}
                      onChange={(e) =>
                        setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))
                      }
                      maxLength={11}
                    />
                  </FormControl>
                  <FormActionButtons
                    primary={
                      <Button
                        colorScheme="blue"
                        size="lg"
                        w="full"
                        onClick={handleSendOtp}
                        isLoading={authLoading}
                        loadingText="در حال ارسال..."
                      >
                        ارسال کد تأیید
                      </Button>
                    }
                    back={backToListButton}
                  />
                </Stack>
              ) : (
                <Stack spacing={4}>
                  <Text fontSize="sm" color="gray.600">
                    کد ارسال‌شده به {phone} را وارد کنید.
                  </Text>
                  <FormControl>
                    <FormLabel>کد تأیید</FormLabel>
                    <Input
                      placeholder="۶ رقمی"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                    />
                  </FormControl>
                  <FormActionButtons
                    primary={
                      <Button
                        colorScheme="blue"
                        size="lg"
                        w="full"
                        onClick={handleVerifyOtp}
                        isLoading={authLoading}
                        loadingText="در حال تأیید..."
                      >
                        تأیید و ادامه
                      </Button>
                    }
                    back={
                      <Stack spacing={3} w="full">
                        <Button variant="outline" size="md" w="full" onClick={() => setAuthStep(1)}>
                          تغییر شماره
                        </Button>
                        {backToListButton}
                      </Stack>
                    }
                  />
                </Stack>
              )}
            </Box>
          )}

          {isAuthed && (
            <>
              <Text fontSize="xs" color="gray.500">
                شمارهٔ تأییدشده: {localStorage.getItem('formFillerPhone') || '—'}
              </Text>

              {total > 0 && !submitted && (
                <Box>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="sm" fontWeight="600" color="teal.700">
                      مرحله {step + 1} از {total}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      هر مرحله یک سؤال
                    </Text>
                  </Flex>
                  <Progress
                    value={progressPercent}
                    size="sm"
                    colorScheme="teal"
                    borderRadius="full"
                    hasStripe
                  />
                </Box>
              )}

              {info && (
                <Box
                  bg={submitted ? 'green.50' : 'blue.50'}
                  color={submitted ? 'green.800' : 'blue.800'}
                  borderRadius="md"
                  fontSize="md"
                  p={4}
                  mb={4}
                  textAlign="center"
                >
                 
                  <Text as="span" flex="1" whiteSpace="pre-wrap" textAlign="center">
                    {info}
                  </Text>
                </Box>
              )}
              {error && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              {submitted ? (
                <FormActionButtons
                  primary={
                    <Button as={Link} to="/auth/forms" colorScheme="blue" size="lg" w="full">
                      بازگشت به لیست فرم‌ها
                    </Button>
                  }
                  back={
                    <Button as={Link} to="/" variant="outline" size="md" w="full">
                      بازگشت به صفحهٔ اصلی
                    </Button>
                  }
                />
              ) : (
                <>
                  {total === 0 ? (
                    <Text fontSize="sm" color="gray.600" textAlign="center">
                      این فرم فیلدی برای نمایش ندارد؛ می‌توانید فقط ذخیره کنید.
                    </Text>
                  ) : (
                    <FormControl isRequired={currentField?.required}>
                      <FormLabel fontSize="md" fontWeight="semibold">
                        {currentField?.label}
                      </FormLabel>
                      {renderFieldInput(currentField)}
                    </FormControl>
                  )}

                  <FormActionButtons
                    primary={
                      isLastStep ? (
                        <Button
                          colorScheme="blue"
                          size="lg"
                          w="full"
                          onClick={handleFinalSave}
                          isLoading={saving}
                          loadingText="در حال ثبت..."
                        >
                          ثبت پاسخ
                        </Button>
                      ) : (
                        <Button colorScheme="blue" size="lg" w="full" onClick={goNext}>
                          مرحله بعد
                        </Button>
                      )
                    }
                    back={
                      <Stack spacing={3} w="full">
                        {step > 0 && (
                          <Button variant="outline" size="md" w="full" onClick={goPrev} isDisabled={saving}>
                            مرحله قبل
                          </Button>
                        )}
                        {backToListButton}
                      </Stack>
                    }
                  />
                </>
              )}
            </>
          )}

          {error && !isAuthed && (
            <Alert status="error" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}

        </Stack>
      </Box>
    </Flex>
  );
}
